"use client";

import { useEffect, useMemo, useState } from "react";
import { Table, Select } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { getOtherProjectsDate } from "../../../redux/otherProjectsSlice/otherProjectsSlice";
import {
    getBusinessSectorWiseExecutionPlan,
    getBusinessSector

} from "../../../redux/unitSlice/unitSlice";
import CustomSelectWithFilter from "../../../components/Common/CustomSelect/CustomSelectWithFilter";
import CustomMultiSelect from "../../../components/Common/CustomSelect/CustomMultiSelect";
import ActiveProject from "../../../assets/icons/Region.svg";
import { PATHNAME } from "../../../pathname/index.js";
import { Skeleton, Spin } from "antd";

const { Option } = Select;

/* ===================== CONSTANTS ===================== */
const YEAR_TOTAL_SUFFIX = "__YEAR_TOTAL";

const headerStyle = {
    background: "#008dd3",
    color: "#fff",
    fontWeight: 600,
    textAlign: "center",
};

const MONTH_ORDER = [
    "Apr", "May", "Jun",
    "Jul", "Aug", "Sep",
    "Oct", "Nov", "Dec",
    "Jan", "Feb", "Mar",
];

/* ===================== UTILS ===================== */
const getFYShortLabel = (year) => {
    const y = Number(year);
    const next = y + 1;

    return `${String(y).slice(-2)}-${String(next).slice(-2)}`;
};

const getPreviousFYQ4Key = () => {
    const today = new Date();

    const currentFYStart =
        today.getMonth() >= 3
            ? today.getFullYear()
            : today.getFullYear() - 1;

    return `Q4-${currentFYStart - 1}`;
};

const getCurrentFYStartYear = () => {
    const today = new Date();

    return today.getMonth() >= 3
        ? today.getFullYear()
        : today.getFullYear() - 1;

    // return "2028"
};

const getQ4Value = (periods) => {
    const q4Key = getPreviousFYQ4Key();

    if (periods[q4Key] !== undefined) {
        return toNumber(periods[q4Key]);
    }

    const fyYear = Number(q4Key.split("-")[1]) + 1;

    return (
        toNumber(periods[`Jan-${fyYear}`]) +
        toNumber(periods[`Feb-${fyYear}`]) +
        toNumber(periods[`Mar-${fyYear}`])
    );
};

const calculateVisibleTotal = (
    row,
    viewType
) => {
    return Object.entries(row)
        .filter(([key]) => {
            return (
                (viewType === "Year" &&
                    /^\d{4}-\d{4}$/.test(key)) ||

                (viewType === "Quarter" &&
                    /^Q[1-4]-\d{4}$/.test(key) &&
                    key !== getPreviousFYQ4Key())
                ||

                (viewType === "Month" &&
                    /^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)-\d{4}$/.test(key))
            );
        })
        .reduce(
            (sum, [, value]) =>
                sum + toNumber(value),
            0
        );

};

const getBalanceTitle = () => {
    const today = new Date();
    const year = today.getFullYear() - 1; // previous year

    const shortYear = String(year).slice(-2);

    return `Order O/s as on 31-12-${shortYear}`;
};

const getAprilBalanceTitle = () => {
    const year = new Date().getFullYear(); // current year
    const shortYear = String(year).slice(-2);

    return `Balance as on 01-04-${shortYear}`;
};
/* ===================== BASE COLUMNS ===================== */

const balanceRevenueColumn = {
    // title: "Order Outstanding as per Dec",
    title: getBalanceTitle(),
    dataIndex: "BALANCE_REVENUE",
    width: 120,
    align: "center",
    render: (val) => {
        // Handle "-" values from API
        if (val === 0 && val !== undefined) return "0";
        if (val === 0 || val === undefined || val === null) return "-";
        return formatNumber(val);
    },
    onHeaderCell: () => ({ style: headerStyle }),
};

const balanceRevenueDifferenceColumn = {
    // title: "Balance as on April",
    title: getAprilBalanceTitle(),
    dataIndex: "BALANCE_REVENUE_DIFF",
    width: 120,
    align: "center",
    render: (val) => {
        if (val === 0 || val === undefined || val === null) return "-";
        return formatNumber(val);
    },
    onHeaderCell: () => ({ style: headerStyle }),
};

const buildYearTotalColumn = (year) => ({
    title: "Total",
    dataIndex: `${year}${YEAR_TOTAL_SUFFIX}`,
    width: 140,
    align: "center",
    render: (val) => formatNumber(val),
    onHeaderCell: () => ({
        style: {
            background: "#006da4",
            color: "#fff",
            fontWeight: 600,
        },
    }),
});

/* ===================== NORMALIZE ===================== */
/* ----------------- CONVERT VALUE TO NUMBER ----------------- */
export const toNumber = (v) =>
    v ? Number(String(v).replace(/,/g, "")) || 0 : 0;

const isMonthKey = (key) =>
    /^[A-Z][a-z]{2}-\d{4}$/.test(key) ||   // Apr-2026
    /^[A-Z][a-z]{2}_\d{2}$/.test(key);     // Apr_26


const isQuarterKey = (key) =>
    /^Q[1-4]-\d{4}$/.test(key) ||        // legacy: Q1-2026
    /^Q[1-4]-\d{4}-\d{4}$/.test(key);

const isYearKey = (key) =>
    /^\d{4}-\d{4}$/.test(key); // 2025-2026


export const getYearFromKey = (key, viewType) => {
    if (/^\d{4}-\d{4}$/.test(key)) {
        return viewType === "Year" ? key : key.split("-")[0];
    }

    // NEW: Q1-2026-2027 → year group is "2026-2027"
    if (/^Q[1-4]-\d{4}-\d{4}$/.test(key)) {
        return `${key.split("-")[1]}-${key.split("-")[2]}`;
    }

    // legacy quarter: Q1-2026 → "2026"
    if (/^Q[1-4]-\d{4}$/.test(key)) {
        return key.split("-")[1];
    }

    if (/^[A-Za-z]{3}-\d{4}$/.test(key)) {
        const [month, year] = key.split("-");
        const y = Number(year);
        if (["Jan", "Feb", "Mar"].includes(month)) {
            return `${y - 1}-${y}`;
        }
        return `${y}-${y + 1}`;
    }

    if (/^[A-Za-z]{3}_\d{2}$/.test(key)) {
        const shortYear = Number(key.split("_")[1]);
        return `20${shortYear}`;
    }

    return null;
};

/* ----------------- FORMAT NUMBER ----------------- */
const nf2 = new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
});
const headerFYLabel = (yearKey) => {
    // Month grouping gives "2024", "2025" etc → show "2024-2025"
    if (/^\d{4}$/.test(yearKey)) {
        const y = Number(yearKey);
        return `${y}-${y + 1}`;
    }

    // Quarter/Year already gives "2024-2025" → keep as-is
    if (/^\d{4}-\d{4}$/.test(yearKey)) return yearKey;

    return yearKey;
};

export const formatNumber = (val) => {
    const num = toNumber(val);
    // keep "-" for truly missing/undefined/null; show 0.00 for 0
    if (val === undefined || val === null || val === "") return "-";
    if (num === 0) return "0";
    return nf2.format(num);
};

// const normalizeData = (data, viewType, isAnticipated = false) => {
//     const result = {};
//     const yearTotals = {};
//     if (!data) return result;

//     Object.entries(data).forEach(([key, value]) => {
//         let normalizedKey = key;

//         // Convert Apr_26 → Apr-2026 (FY-aware)
//         if (/^[A-Za-z]{3}_\d{2}$/.test(key)) {
//             const [month, yy] = key.split("_");
//             // Do NOT adjust year here — keep as calendar year
//             // Jan_27 → Jan-2027, Mar_27 → Mar-2027
//             normalizedKey = `${month}-20${yy}`;
//         }

//         if (
//             ["PROJ_NAME", "IO_NO", "REV_NO", "description", "upto", "Total"].includes(key)
//         ) return;

//         const num = toNumber(value);
//         if (num === undefined || num === null) return;

//         const year = getYearFromKey(
//             normalizedKey,
//             viewType
//         );

//         if (year) {
//             const yearStart =
//                 /^\d{4}-\d{4}$/.test(year)
//                     ? Number(year.split("-")[0])
//                     : Number(year);


//             // if (
//             //     yearStart <
//             //     getCurrentFYStartYear()
//             // ) {
//             //     return;
//             // }

//             const prevQ4Key =
//                 getPreviousFYQ4Key();

//             if (
//                 yearStart <
//                 getCurrentFYStartYear()
//             ) {
//                 // allow ONLY previous FY Q4
//                 if (
//                     !(
//                         viewType === "Quarter" &&
//                         normalizedKey === prevQ4Key
//                     )
//                 ) {
//                     return;
//                 }
//             }



//         }


//         // MONTH -> QUARTER CONVERSION for anticipated
//         // if (viewType === "Quarter" && isMonthKey(normalizedKey)) {
//         if (
//             isAnticipated &&
//             viewType === "Quarter" &&
//             isMonthKey(normalizedKey)
//         ) {
//             const [month, yearStr] = normalizedKey.split("-");
//             const year = Number(yearStr);

//             let quarter = "";
//             let fyStartYear;

//             if (["Apr", "May", "Jun"].includes(month)) { quarter = "Q1"; fyStartYear = year; }
//             else if (["Jul", "Aug", "Sep"].includes(month)) { quarter = "Q2"; fyStartYear = year; }
//             else if (["Oct", "Nov", "Dec"].includes(month)) { quarter = "Q3"; fyStartYear = year; }
//             else if (["Jan", "Feb", "Mar"].includes(month)) { quarter = "Q4"; fyStartYear = year - 1; }

//             normalizedKey = `${quarter}-${fyStartYear}`;
//         }

//         if (viewType === "Month" && !isMonthKey(normalizedKey)) return;
//         if (viewType === "Quarter" && !isQuarterKey(normalizedKey)) return;
//         if (viewType === "Year" && !isYearKey(normalizedKey)) return;

//         result[normalizedKey] = (result[normalizedKey] || 0) + num;

//         if (viewType === "Quarter") {
//             yearTotals[normalizedKey.split("-")[1]] =
//                 (yearTotals[normalizedKey.split("-")[1]] || 0) + num;
//         }
//         if (viewType === "Month") {
//             const fyYear = getYearFromKey(normalizedKey, viewType);
//             yearTotals[fyYear] = (yearTotals[fyYear] || 0) + num;
//         }
//     });

//     result.ROW_OVERALL_TOTAL = Object.values(result)
//         .filter((_, i) => !Object.keys(result)[i]?.endsWith("__YEAR_TOTAL"))
//         .reduce((a, b) => a + b, 0);

//     Object.entries(yearTotals).forEach(([y, v]) => {
//         result[`${y}${YEAR_TOTAL_SUFFIX}`] =
//             viewType === "Year" && data?.TOTAL_SUM != null
//                 ? toNumber(data.TOTAL_SUM)
//                 : v;
//     });

//     return result;
// };

const normalizeData = (data, viewType, isAnticipated = false) => {
    const result = {};
    const yearTotals = {};
    if (!data) return result;

    // ✅ Anticipated orders in Year view carry BOTH a raw, unscaled year total
    // (e.g. "2026-2027": 950) AND a correctly-scaled "_formatted" counterpart
    // (e.g. "2026-2027_formatted": "9.50"). Remap to the formatted value and
    // drop the raw one — same fix already applied to direct_orders.
    let sourceData = data;
    if (isAnticipated && viewType === "Year") {
        sourceData = {};
        Object.entries(data).forEach(([key, value]) => {
            const formattedMatch = key.match(/^(\d{4}-\d{4})_formatted$/);
            if (formattedMatch) {
                sourceData[formattedMatch[1]] = value; // e.g. "2026-2027": "9.50"
                return;
            }

            // Skip the raw unscaled year total — the formatted twin above wins
            if (/^\d{4}-\d{4}$/.test(key)) return;

            sourceData[key] = value;
        });
    }

    Object.entries(sourceData).forEach(([key, value]) => {
        let normalizedKey = key;

        // Convert Apr_26 → Apr-2026 (FY-aware)
        if (/^[A-Za-z]{3}_\d{2}$/.test(key)) {
            const [month, yy] = key.split("_");
            normalizedKey = `${month}-20${yy}`;
        }

        if (
            ["PROJ_NAME", "IO_NO", "REV_NO", "description", "upto", "Total"].includes(key)
        ) return;

        const num = toNumber(value);
        if (num === undefined || num === null) return;

        const year = getYearFromKey(normalizedKey, viewType);

        if (year) {
            const yearStart =
                /^\d{4}-\d{4}$/.test(year)
                    ? Number(year.split("-")[0])
                    : Number(year);

            const prevQ4Key = getPreviousFYQ4Key();

            if (yearStart < getCurrentFYStartYear()) {
                if (!(viewType === "Quarter" && normalizedKey === prevQ4Key)) {
                    return;
                }
            }
        }

        // MONTH -> QUARTER CONVERSION for anticipated
        if (isAnticipated && viewType === "Quarter" && isMonthKey(normalizedKey)) {
            const [month, yearStr] = normalizedKey.split("-");
            const year = Number(yearStr);

            let quarter = "";
            let fyStartYear;

            if (["Apr", "May", "Jun"].includes(month)) { quarter = "Q1"; fyStartYear = year; }
            else if (["Jul", "Aug", "Sep"].includes(month)) { quarter = "Q2"; fyStartYear = year; }
            else if (["Oct", "Nov", "Dec"].includes(month)) { quarter = "Q3"; fyStartYear = year; }
            else if (["Jan", "Feb", "Mar"].includes(month)) { quarter = "Q4"; fyStartYear = year - 1; }

            normalizedKey = `${quarter}-${fyStartYear}`;
        }

        if (viewType === "Month" && !isMonthKey(normalizedKey)) return;
        if (viewType === "Quarter" && !isQuarterKey(normalizedKey)) return;
        if (viewType === "Year" && !isYearKey(normalizedKey)) return;

        result[normalizedKey] = (result[normalizedKey] || 0) + num;

        if (viewType === "Quarter") {
            yearTotals[normalizedKey.split("-")[1]] =
                (yearTotals[normalizedKey.split("-")[1]] || 0) + num;
        }
        if (viewType === "Month") {
            const fyYear = getYearFromKey(normalizedKey, viewType);
            yearTotals[fyYear] = (yearTotals[fyYear] || 0) + num;
        }
    });

    result.ROW_OVERALL_TOTAL = Object.values(result)
        .filter((_, i) => !Object.keys(result)[i]?.endsWith("__YEAR_TOTAL"))
        .reduce((a, b) => a + b, 0);

    Object.entries(yearTotals).forEach(([y, v]) => {
        result[`${y}${YEAR_TOTAL_SUFFIX}`] =
            viewType === "Year" && sourceData?.TOTAL_SUM != null
                ? toNumber(sourceData.TOTAL_SUM)
                : v;
    });

    return result;
};

export const buildProjectRow = (
    project,
    viewType,
    sectorName,
    orderType
) => {
    const norm = normalizeData(
        project,
        viewType,
        orderType === "Anticipated Orders"
    );
    const uniqueId =
        project.IO_NO ||
        project.ORDER_ID ||
        project.ID ||
        project.PROJECT_TITLE;

    const revision =
        project.REV_NO || "NO_REV";

    const row = {
        key:
            `${sectorName}_${orderType}_` +
            `${uniqueId}_${revision}`,

        label:
            project.PROJ_NAME ||
            project.PROJECT_TITLE,

        BALANCE_REVENUE:
            project.BALANCE_REVENUE === "-"
                ? "-"
                : toNumber(project.BALANCE_REVENUE),

        BALANCE_REVENUE_DIFF:
            toNumber(project.BALANCE_REVENUE) -
            getQ4Value(project),

    };

    Object.entries(norm)?.forEach(([k, v]) => {
        row[k] = v;
    });
    row.total = calculateVisibleTotal(
        row,
        viewType
    );


    return row;

};

/* ===================== COMPONENT ===================== */

const BusinessSectorWiseExecutionPlan = () => {
    const dispatch = useDispatch();
    const { businessSectorWiseExecutionPlan, businessSectorListWithoutAll } = useSelector((state) => state.unit);
    const { user } = useSelector((state) => state.auth);
    const { systemDate } = useSelector((state) => state.otherProjects);
    const restrictedUser = ["unit", "region"].includes(user?.role?.toLowerCase());

    const [viewType, setViewType] = useState("Quarter");
    const [selectedBusinessSector, setSelectedBusinessSector] = useState([]);
    const [pageLoading, setPageLoading] = useState(true);

    const [parameter, setParameter] = useState("PROJECT_COST");
    //  ADD THESE HELPERS (just below MONTH_ORDER or near utils)
    const fySort = (a, b) => Number(a.split("-")[0]) - Number(b.split("-")[0]);



    // const getQuarterLabel = (key) => key.split("-")[0]; // Q1-2025 => Q1
    const getQuarterLabel = (key) => key.split("-")[0];

    // useEffect(() => {
    //     dispatch(
    //         getBusinessSectorWiseExecutionPlan({
    //             FILTER_PARAMETER: parameter,
    //             BS_UNIT:
    //                 Array.isArray(selectedBusinessSector) &&
    //                     selectedBusinessSector.length
    //                     ? selectedBusinessSector.join(",")
    //                     : null,

    //         })
    //     );
    // }, [dispatch, parameter, selectedBusinessSector]);

    useEffect(() => {
        let isMounted = true;

        setPageLoading(true);

        dispatch(
            getBusinessSectorWiseExecutionPlan({
                FILTER_PARAMETER: parameter,
                BS_UNIT:
                    selectedBusinessSector.length
                        ? selectedBusinessSector.join(",")
                        : null,
                notifyWithFilter: true,
            })
        ).finally(() => {
            if (isMounted) {
                setPageLoading(false);
            }
        });

        return () => {
            isMounted = false;
        };
    }, [dispatch, parameter, selectedBusinessSector]);

    useEffect(() => {
        dispatch(getBusinessSector());
    }, [dispatch]);

    useEffect(() => {
        dispatch(getOtherProjectsDate("system"));
    }, [dispatch]);


    /* ---------- DATA ---------- */

    const buildOrderGroupRow = (
        label,
        projects = [],
        viewType,
        sectorName
    ) => {
        const children = projects.map((project) =>
            buildProjectRow(
                project,
                viewType,
                sectorName,
                label
            )
        );
        const combined = {};

        children.forEach((child) => {
            Object.entries(child).forEach(([key, value]) => {
                if (
                    [
                        "key",
                        "label",
                        "children",
                        "BALANCE_REVENUE",
                    ].includes(key)
                ) {
                    return;
                }

                combined[key] =
                    (combined[key] || 0) + toNumber(value);
            });
        });

        const balanceRevenue = projects.reduce(
            (sum, p) =>
                sum + toNumber(p.BALANCE_REVENUE),
            0
        );
        const q4Value = projects.reduce(
            (sum, p) =>
                sum + getQ4Value(p),
            0
        );


        return {
            key: `${sectorName}_${label}`,
            label,
            isOrderGroupRow: true,
            BALANCE_REVENUE: balanceRevenue,
            BALANCE_REVENUE_DIFF: balanceRevenue - q4Value,
            total: calculateVisibleTotal(
                combined,
                viewType
            ),

            ...combined,
            children,
        };

    };

    const buildOtherProjectsRow = (
        otherProjects,
        viewType,
        sector
    ) => {
        const normalized = normalizeData(
            otherProjects,
            viewType
        );


        return {
            key: `${sector}_OTHER_PROJECTS`,
            label: (
                <span
                    className="text-blue-600 underline cursor-pointer"
                    onClick={(e) => {
                        e.stopPropagation();
                        const params =
                            new URLSearchParams({
                                BS_UNIT: sector,
                                FILTER_PARAMETER: parameter,
                                VIEW_TYPE: viewType,
                            });

                        // window.open(
                        //     `/consolidatedreports/business-sector-other-projects?${params.toString()}`,
                        //     "_blank"
                        // );
                        window.open(
                            `${PATHNAME.ADMIN_CONSOLIDATED_BUSINESS_SECTOR_OTHER_PROJECTS}?${params.toString()}`,
                            "_blank"
                        );

                    }}
                >
                    Other Projects
                </span>


            ),

            isOtherProjects: true,

            BALANCE_REVENUE:
                toNumber(otherProjects.BALANCE_REVENUE),

            BALANCE_REVENUE_DIFF:
                toNumber(otherProjects.BALANCE_REVENUE) -
                getQ4Value(otherProjects),


            total: calculateVisibleTotal(
                normalized,
                viewType
            ),


            ...normalized,
        };

    };

    const buildSectorRow = (
        sector,
        viewType
    ) => {
        const children = [];


        if (sector.clear_orders?.length) {
            children.push(
                buildOrderGroupRow(
                    "Clear Orders",
                    sector.clear_orders,
                    viewType,
                    sector.BS_UNIT_NAME
                )
            );
        }

        if (sector.hold_orders?.length) {
            children.push(
                buildOrderGroupRow(
                    "Hold Orders",
                    sector.hold_orders,
                    viewType,
                    sector.BS_UNIT_NAME
                )
            );
        }

        if (sector.anticipated_orders?.length) {
            children.push(
                buildOrderGroupRow(
                    "Anticipated Orders",
                    sector.anticipated_orders,
                    viewType,
                    sector.BS_UNIT_NAME
                )
            );
        }

        if (sector.other_projects) {
            children.push(
                buildOtherProjectsRow(
                    sector.other_projects,
                    viewType,
                    sector.BS_UNIT
                )
            );
        }

        const combined = {};

        children.forEach((child) => {
            Object.entries(child).forEach(([key, value]) => {
                if (
                    [
                        "key",
                        "label",
                        "children",
                        "BALANCE_REVENUE",
                    ].includes(key)
                ) {
                    return;
                }

                combined[key] =
                    (combined[key] || 0) +
                    toNumber(value);
            });
        });

        const balanceRevenue = children.reduce(
            (sum, child) =>
                sum + toNumber(child.BALANCE_REVENUE),
            0
        );

        return {
            key: sector.BS_UNIT,
            label: sector.BS_UNIT_NAME,
            isSectorRow: true,

            BALANCE_REVENUE: balanceRevenue,

            total: calculateVisibleTotal(
                combined,
                viewType
            ),


            ...combined,

            children,
        };


    };

    const buildGrandTotalRow = (rows) => {
        const combined = {};

        rows.forEach((row) => {
            Object.entries(row).forEach(([key, value]) => {

                if (
                    key === "key" ||
                    key === "label" ||
                    key === "children" ||
                    key === "projectMap" ||
                    key === "isSubtotal"

                    // EXCLUDE STATIC REVENUE COLS
                    // key === "BALANCE_REVENUE" ||
                    // key === "BALANCE_REVENUE_DIFF"
                ) {
                    return;
                }

                combined[key] =
                    (combined[key] || 0) +
                    toNumber(value);
            });
        });

        return {
            key: "GRAND_TOTAL",
            label: "Grand Total",
            isGrandTotal: true,

            ...Object.fromEntries(
                Object.entries(combined).map(([k, v]) => [
                    k,
                    formatNumber(v),
                ])
            ),
        };
    };

    // const buildDirectOrdersRow = (
    //     directOrders,
    //     viewType
    // ) => {
    //     if (!directOrders) return null;

    //     let sourceData = {};

    //     if (viewType === "Month") {
    //         sourceData = directOrders.months || {};
    //     } else if (viewType === "Quarter") {
    //         sourceData = directOrders.quarters || {};
    //     } else {
    //         sourceData = directOrders;
    //     }

    //     const normalized = normalizeData(
    //         sourceData,
    //         viewType
    //     );

    //     return {
    //         key: "DIRECT_ORDERS",
    //         label: "Direct Orders",
    //         isDirectOrders: true,

    //         total: calculateVisibleTotal(
    //             normalized,
    //             viewType
    //         ),

    //         ...normalized,
    //     };
    // };

    const buildDirectOrdersRow = (
        directOrders,
        viewType
    ) => {
        if (!directOrders) return null;

        let sourceData = {};

        if (viewType === "Month") {
            sourceData = directOrders.months || {};
        } else if (viewType === "Quarter") {
            sourceData = directOrders.quarters || {};
        } else {
            // ✅ Year view: directOrders itself carries the year keys,
            // but raw keys (e.g. "2026-2027": 5048400) are 100x inflated
            // vs. the correctly-scaled "_formatted" counterpart.
            Object.entries(directOrders).forEach(([key, value]) => {
                // Skip raw unscaled year totals
                if (/^\d{4}-\d{4}$/.test(key)) return;

                // Remap "_formatted" year totals to bare year key
                const formattedMatch = key.match(/^(\d{4}-\d{4})_formatted$/);
                if (formattedMatch) {
                    sourceData[formattedMatch[1]] = value; // e.g. "2026-2027": "50,484.00"
                    return;
                }

                // Skip nested months/quarters objects — irrelevant for Year view
                if (key === "months" || key === "quarters") return;

                sourceData[key] = value;
            });
        }

        const normalized = normalizeData(
            sourceData,
            viewType
        );

        return {
            key: "DIRECT_ORDERS",
            label: "Direct Orders",
            isDirectOrders: true,

            total: calculateVisibleTotal(
                normalized,
                viewType
            ),

            ...normalized,
        };
    };

    const firstQuarter =
        viewType === "Quarter"
            ? getPreviousFYQ4Key()
            : null;

    const tableData = useMemo(() => {
        if (
            !businessSectorWiseExecutionPlan?.business_sectors
        ) {
            return [];
        }


        const sectorRows =
            businessSectorWiseExecutionPlan.business_sectors.map(
                (sector) =>
                    buildSectorRow(
                        sector,
                        viewType
                    )
            );

        // return [
        //     grandTotal,
        //     ...sectorRows,
        // ];

        const directOrdersRow =
            !selectedBusinessSector?.length &&
                businessSectorWiseExecutionPlan?.direct_orders
                ? buildDirectOrdersRow(
                    businessSectorWiseExecutionPlan.direct_orders,
                    viewType
                )
                : null;

        const finalRows = [
            ...sectorRows,
            ...(directOrdersRow ? [directOrdersRow] : []),
        ];

        const grandTotal =
            buildGrandTotalRow(finalRows);

        return [
            grandTotal,
            ...finalRows,
        ];


    }, [
        businessSectorWiseExecutionPlan,
        viewType,
    ]);


    /* ---------- FLATTEN ---------- */

    const rows = useMemo(() => {
        const flat = [];
        const walk = (r) => {
            flat.push(r);
            r.children?.forEach(walk);
        };
        tableData?.forEach(walk);
        return flat;
    }, [tableData]);


    const groupedKeys = useMemo(() => {
        const map = {};
        const prevQ4Key = getPreviousFYQ4Key(); // Q4-2025
        const currentFYStartYear = getCurrentFYStartYear(); // 2026

        if (!rows || !Array.isArray(rows)) return map;

        rows.forEach((r) => {
            if (!r || typeof r !== "object") return;
            Object.keys(r).forEach((k) => {
                const valid =
                    (viewType === "Year" && /^\d{4}-\d{4}$/.test(k)) ||
                    (viewType === "Quarter" && /^Q[1-4]-\d{4}$/.test(k)) ||
                    (viewType === "Month" &&
                        /^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)-\d{4}$/.test(k));

                if (!valid) return;
                if (k === prevQ4Key) return; // standalone col, not in group

                const year = getYearFromKey(k, viewType);
                if (!year) return;

                const yearStart = /^\d{4}-\d{4}$/.test(year)
                    ? Number(year.split("-")[0])
                    : Number(year);

                // ✅ Only current FY
                if (yearStart < currentFYStartYear) return;
                // if (yearStart > currentFYStartYear) return;

                if (!map[year]) map[year] = new Set();
                map[year].add(k);
            });
        });

        Object.keys(map).forEach((y) => {
            if (!map[y]) return;
            map[y] = Array.from(map[y]).sort((a, b) => {
                if (viewType === "Month") {
                    return MONTH_ORDER.indexOf(a.split("-")[0]) - MONTH_ORDER.indexOf(b.split("-")[0]);
                }
                return a.localeCompare(b);
            });
        });

        return map;
    }, [rows, viewType]);

    /* ---------- COLUMNS ---------- */
    //  REPLACE ONLY THIS columns useMemo (your existing one)
    const keys = [
        ...new Set(
            rows.flatMap(row => Object.keys(row))
        ),
    ];
    const yearColumns = keys
        .filter(key => /^\d{4}-\d{4}$/.test(key))
        .sort();

    const quarterColumns = keys
        .filter(key => /^Q[1-4]-\d{4}$/.test(key))
        .sort((a, b) => {
            const [qA, yA] = a.split("-");
            const [qB, yB] = b.split("-");

            if (yA !== yB) {
                return Number(yA) - Number(yB);
            }

            return Number(qA.replace("Q", "")) -
                Number(qB.replace("Q", ""));
        });

    const monthOrder = {
        Apr: 1,
        May: 2,
        Jun: 3,
        Jul: 4,
        Aug: 5,
        Sep: 6,
        Oct: 7,
        Nov: 8,
        Dec: 9,
        Jan: 10,
        Feb: 11,
        Mar: 12,
    };

    const monthColumns = keys
        .filter(key =>
            /^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)-\d{4}$/.test(key)
        )
        .sort((a, b) => {
            const [monthA, yearA] = a.split("-");
            const [monthB, yearB] = b.split("-");

            const fyA =
                ["Jan", "Feb", "Mar"].includes(monthA)
                    ? Number(yearA) - 1
                    : Number(yearA);

            const fyB =
                ["Jan", "Feb", "Mar"].includes(monthB)
                    ? Number(yearB) - 1
                    : Number(yearB);

            if (fyA !== fyB) {
                return fyA - fyB;
            }

            return monthOrder[monthA] - monthOrder[monthB];
        });

    const columns = useMemo(() => {
        const cols = [
            {
                title: "Business Sectors",
                dataIndex: "label",
                fixed: "left",
                width: 460,
                className: "orders-column",
                render: (text) => <strong>{text}</strong>,
                onHeaderCell: () => ({ style: headerStyle }),
            },
        ];

        if (parameter === "REVENUE") {
            const dynamicBalanceTitle = systemDate?.STARTDATE
                ? `Order as on ${systemDate.STARTDATE}`
                : getBalanceTitle();

            const dynamicBalanceRevenueColumn = {
                ...balanceRevenueColumn,
                title: dynamicBalanceTitle,
            };

            // 1. Order O/s / Order as on (system date)
            cols.push(dynamicBalanceRevenueColumn);

            // 2. Q4/25-26 — Quarter view only (Commented out)
            // if (viewType === "Quarter" && firstQuarter) {
            //     cols.push({
            //         title: `Q4/${getFYShortLabel(Number(firstQuarter.split("-")[1]))}`,
            //         dataIndex: firstQuarter,
            //         align: "center",
            //         width: 140,
            //         render: (val) => formatNumber(val),
            //         onHeaderCell: () => ({ style: headerStyle }),
            //     });
            // }

            // 3. Balance as on — always (BALANCE_REVENUE - Q4, view-agnostic) (Commented out)
            // cols.push(balanceRevenueDifferenceColumn);
        }

        const groups = Object.keys(groupedKeys).sort(fySort);

        groups.forEach((year) => {
            let keys = groupedKeys[year] || [];

            if (viewType === "Quarter" && firstQuarter) {
                keys = keys.filter((k) => k !== firstQuarter);
            }

            if (viewType === "Year") {
                keys.forEach((k) => {
                    cols.push({
                        title: k,
                        dataIndex: k,
                        align: "center",
                        width: 140,
                        render: (val) => formatNumber(val),
                        onHeaderCell: () => ({ style: headerStyle }),
                    });
                });
                return;
            }

            cols.push({
                title: headerFYLabel(year),
                align: "center",
                onHeaderCell: () => ({ style: headerStyle }),
                children: [
                    ...keys.map((k, i) => ({
                        title: viewType === "Quarter" ? getQuarterLabel(k) : k,
                        dataIndex: k,
                        key: `${year}_${k}_${i}`,
                        align: "center",
                        width: 140,
                        render: (val) => formatNumber(val),
                    })),
                    buildYearTotalColumn(year),
                ],
            });
        });

        cols.push({
            title: "Overall Total",
            dataIndex: "total",
            fixed: "right",
            width: 120,
            align: "center",
            className: "total-column",
            render: (val) => formatNumber(val),
            onHeaderCell: () => ({ style: headerStyle }),
        });

        return cols;
    }, [groupedKeys, viewType, parameter, firstQuarter, systemDate]);

    /* ===================== RENDER ===================== */

    return (
        <div className="p-5">
            <div className="flex items-center gap-3">
                <div className="flex gap-3 items-center">
                    <img src={ActiveProject} alt="region" />
                    <div>
                        <div className="font-inter font-semibold text-[24px] text-[#101828]">
                            Execution Plan - Business Sector Wise
                        </div>
                        <div className="text-gray-500 text-sm">
                            * All figures are in Rupees (in Crores)
                        </div>
                    </div>
                </div>


            </div>

            <div className="mt-6 bg-white shadow-lg rounded-xl p-4">
                <div className="flex justify-between mb-4">
                    <div>
                        {/* <div className="font-semibold">BHEL Level Performance Plan - Execution Plan</div> */}
                        {/* <div className="text-sm text-gray-500">
              * All figures are in Rupees (in lakhs)
            </div> */}
                    </div>

                    <div className="flex gap-3">
                        <CustomMultiSelect
                            data={[
                                ...(businessSectorListWithoutAll || []).map((item) => ({
                                    label: item.BS_UNIT_DESC,
                                    value: item.BS_UNIT,
                                })),
                            ]}
                            placeholder="Business Sector"
                            value={selectedBusinessSector}
                            onChange={setSelectedBusinessSector}
                            className="w-[18rem]"
                        />


                        <Select
                            value={viewType}
                            onChange={setViewType}
                            style={{ width: 160 }}
                        >
                            <Option value="Year">Year Wise</Option>
                            <Option value="Quarter">Quarter Wise</Option>
                            <Option value="Month">Month Wise</Option>
                        </Select>

                        <CustomSelectWithFilter
                            data={[
                                { label: "Revenue", value: "REVENUE" },
                                { label: "Project cost", value: "PROJECT_COST" },
                                { label: "Profit/Loss", value: "PROFIT_LOSS" },
                                { label: "Apportioned cost", value: "APPORTIONED_COST" },
                                { label: "Operational income", value: "OPERATIONAL_INCOME" },
                                { label: "Project profit", value: "PROJECT_PROFIT" },
                                { label: "Net billing", value: "NET_BILLING" },
                                { label: "Cash inflow", value: "CASH_INFLOW" },
                                { label: "Cash outflow", value: "CASH_OUTFLOW" },
                                { label: "Net cash flow", value: "NET_CASH_FLOW" }
                            ]}
                            onChange={setParameter}
                            placeholder="PROJECT_COST"
                            className="w-[14rem]"
                        />
                    </div>
                </div>

                {pageLoading ? (
                    <div className="flex items-center justify-center h-[600px]">
                        <Spin size="large" />
                        {/* <Skeleton active paragraph={{ rows: 12 }} /> */}
                    </div>
                ) : (
                    <Table
                        bordered
                        pagination={false}
                        scroll={{ x: "max-content", y: 800 }}
                        className="execution-plan-table other-projects-table"
                        expandable={{
                            rowExpandable: (row) => !!row.children?.length,
                        }}

                        rowClassName={(row) => {
                            if (row.isGrandTotal) {
                                return "grand-total-row";
                            }

                            if (row.isSectorRow) {
                                return "business-sector-row";
                            }

                            if (row.isOrderGroupRow) {
                                return "subtotal-row";
                            }

                            if (row.isOtherProjects) {
                                return "subtotal-row";
                            }

                            if (row.isDirectOrders) {
                                return "subtotal-row";
                            }

                            return "project-row";
                        }}
                        columns={columns}
                        dataSource={tableData}
                        rowKey="key"
                    />
                )}
            </div>
        </div >
    );
};

export default BusinessSectorWiseExecutionPlan;
